import React, { useState } from "react";
import {
    StatusIndicator,
    StatusIndicatorVariant,
} from "@design-system/StatusIndicator/StatusIndicator";
import { HStack } from "@design-system/HStack/HStack";
import type { Side } from "@design-system/overlay/overlayPositioning";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { VStack } from "@design-system/VStack/VStack";
import {
    BooleanControl,
    PlaygroundSelect,
    TextControl,
    VariantGrid,
    VariantItem,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./FeedbackPlayground.module.scss";
import gridStyles from "./playgroundControls.module.scss";

const statusVariants = [
    "gray",
    "yellow",
    "green",
    "blue",
    "purple",
    "red",
] as const satisfies readonly StatusIndicatorVariant[];

const appearances = ["outlined", "solid"] as const;

type Appearance = (typeof appearances)[number];

const tooltipSides = [
    "top",
    "right",
    "bottom",
    "left",
] as const satisfies readonly Side[];

type TooltipSide = (typeof tooltipSides)[number];

const invoiceStatusLabels: Record<StatusIndicatorVariant, string> = {
    gray: "Draft",
    yellow: "Open",
    green: "Ready",
    blue: "Paid",
    purple: "Void",
    red: "Uncollectable",
};

export const StatusIndicatorPlayground = () => {
    const [variant, setVariant] = useState<StatusIndicatorVariant>("blue");
    const [appearance, setAppearance] = useState<Appearance>("outlined");
    const [hasText, setHasText] = useState(true);
    const [label, setLabel] = useState("Paid");
    const [tooltipLabel, setTooltipLabel] = useState("");
    const [tooltipSide, setTooltipSide] = useState<TooltipSide>("top");

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Variant</h2>
                        <PlaygroundSelect<StatusIndicatorVariant>
                            options={statusVariants}
                            value={variant}
                            onChange={setVariant}
                        />
                        <h2 className={styles.subheading}>Appearance</h2>
                        <ToggleGroup.Root
                            value={appearance}
                            onValueChange={(value) => {
                                if (value === "outlined" || value === "solid") {
                                    setAppearance(value);
                                }
                            }}
                        >
                            {appearances.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Content</h2>
                        <div className={styles.fields}>
                            <TextControl
                                label="Label"
                                value={label}
                                onChange={setLabel}
                            />
                            <BooleanControl
                                label="Show text"
                                checked={hasText}
                                onChange={setHasText}
                            />
                        </div>
                    </section>
                    <section>
                        <h2>Tooltip</h2>
                        <p className={styles.hint}>
                            The dot only wraps itself in a tooltip when
                            &ldquo;Show text&rdquo; is off. With text visible
                            both tooltip props are ignored.
                        </p>
                        <div className={styles.fields}>
                            <TextControl
                                label="Tooltip label"
                                value={tooltipLabel}
                                onChange={setTooltipLabel}
                                placeholder="Falls back to label"
                            />
                            <div className={styles.field}>
                                <span>Tooltip side</span>
                                <PlaygroundSelect<TooltipSide>
                                    options={tooltipSides}
                                    value={tooltipSide}
                                    onChange={setTooltipSide}
                                />
                            </div>
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${variant} · ${appearance}`}
                width="full"
                output={JSON.stringify(
                    {
                        componentProps: {
                            variant,
                            label,
                            appearance,
                            showText: hasText,
                            tooltipLabel: tooltipLabel || null,
                            tooltipSide,
                        },
                        showcaseState: {},
                    },
                    null,
                    2,
                )}
            >
                <VStack gap={16} className={gridStyles.fillWidth}>
                    <HStack justify="center">
                        <StatusIndicator
                            variant={variant}
                            label={label}
                            appearance={appearance}
                            showText={hasText}
                            tooltipLabel={tooltipLabel || undefined}
                            tooltipSide={tooltipSide}
                        />
                    </HStack>
                    <VariantGrid title="Variants" columns="auto">
                        {statusVariants.map((option) => (
                            <VariantItem key={option} label={option}>
                                <StatusIndicator
                                    variant={option}
                                    label={invoiceStatusLabels[option]}
                                    appearance={appearance}
                                    showText={hasText}
                                />
                            </VariantItem>
                        ))}
                    </VariantGrid>
                    <VariantGrid title="Appearance" columns="auto">
                        {appearances.map((option) => (
                            <VariantItem key={option} label={option}>
                                <StatusIndicator
                                    variant={variant}
                                    label={label}
                                    appearance={option}
                                    showText={hasText}
                                />
                            </VariantItem>
                        ))}
                    </VariantGrid>
                </VStack>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
